/**
 * IP Whitelist API Client
 *
 * ISO 27001 A.9.4.2 - Secure log-on procedures (network access control)
 */

import { fetchWithAuth } from '@/utils/api';

export type IpWhitelistMode = 'STRICT' | 'WARN' | 'LOG';

export interface IpWhitelistConfig {
  enabled: boolean;
  mode: IpWhitelistMode;
}

export interface IpWhitelistEntry {
  id: string;
  tenantId: string;
  ipAddressOrRange: string;
  label: string;
  description?: string;
  isActive: boolean;
  expiresAt?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface CurrentIpInfo {
  ip: string;
  isPrivate: boolean;
  suggestedLabel: string;
}

export interface IpCheckResult {
  allowed: boolean;
  mode: IpWhitelistMode;
  reason?: string;
  checkedIp: string;
  matchedEntry?: {
    id: string;
    label: string;
    ipAddressOrRange: string;
  };
}

export interface CreateIpWhitelistEntry {
  ipAddressOrRange: string;
  label: string;
  description?: string;
  expiresAt?: string;
}

export interface UpdateIpWhitelistEntry {
  label?: string;
  description?: string;
  isActive?: boolean;
  expiresAt?: string | null;
}

const API_BASE = '/api/v1/core/ip-whitelist';

export const ipWhitelistApi = {
  /**
   * Get whitelist configuration (enabled, mode)
   */
  async getConfig(): Promise<IpWhitelistConfig> {
    const response = await fetchWithAuth(`${API_BASE}/config`, {
    });
    const result = await response.json();
    return result.data;
  },

  /**
   * Update whitelist configuration (ADMIN+ required)
   */
  async updateConfig(config: Partial<IpWhitelistConfig>): Promise<void> {
    await fetchWithAuth(`${API_BASE}/config`, {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  },

  /**
   * Get all whitelist entries
   */
  async getEntries(): Promise<IpWhitelistEntry[]> {
    const response = await fetchWithAuth(API_BASE, {
    });
    const result = await response.json();
    return result.data;
  },

  /**
   * Add a new whitelist entry (ADMIN+ required)
   */
  async addEntry(entry: CreateIpWhitelistEntry): Promise<IpWhitelistEntry> {
    const response = await fetchWithAuth(API_BASE, {
      method: 'POST',
      body: JSON.stringify(entry),
    });
    const result = await response.json();
    return result.data;
  },

  /**
   * Update a whitelist entry (ADMIN+ required)
   */
  async updateEntry(id: string, update: UpdateIpWhitelistEntry): Promise<IpWhitelistEntry> {
    const response = await fetchWithAuth(`${API_BASE}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(update),
    });
    const result = await response.json();
    return result.data;
  },

  /**
   * Delete a whitelist entry (ADMIN+ required)
   */
  async deleteEntry(id: string): Promise<void> {
    await fetchWithAuth(`${API_BASE}/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Get current user's IP info (for quick-add)
   */
  async getCurrentIp(): Promise<CurrentIpInfo> {
    const response = await fetchWithAuth(`${API_BASE}/current-ip`, {
    });
    const result = await response.json();
    return result.data;
  },

  /**
   * Check if an IP is allowed (for testing)
   */
  async checkIp(ipAddress?: string): Promise<IpCheckResult> {
    const response = await fetchWithAuth(`${API_BASE}/check`, {
      method: 'POST',
      body: JSON.stringify({ ipAddress }),
    });
    const result = await response.json();
    return result.data;
  },
};

export default ipWhitelistApi;
